// apps/web/src/pages/UserManagementPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import { getAuth } from "firebase/auth";
import { apiGet, apiPost } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { SupportAnchorBanner } from "../components/SupportAnchorBanner";
import { UserManagementSupportInfo } from "../components/UserManagementSupportInfo";

type UserItem = {
    uid: string;
    email: string | null;
    displayName: string | null;
    role: string | null;
    status: string | null;
    joinedAt: string | null;
    lastSignInAt: string | null;
};

const ROLE_OPTIONS = [
    { value: "admin", label: "管理者" },
    { value: "member", label: "メンバー" },
];

function isProbablyGmail(email: string) {
    const e = email.trim().toLowerCase();
    return e.endsWith("@gmail.com") || e.endsWith("@googlemail.com");
}

export default function UserManagementPage() {
    const currentUid = getAuth().currentUser?.uid ?? null;

    const [users, setUsers] = useState<UserItem[]>([]);
    const [pendingUsers, setPendingUsers] = useState<UserItem[]>([]);

    const [loading, setLoading] = useState(true);
    const [loadingPending, setLoadingPending] = useState(true);

    const [error, setError] = useState<string | null>(null);

    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("member");
    const [saving, setSaving] = useState(false);

    const [updatingUid, setUpdatingUid] = useState<string | null>(null);

    // ✅ 追加：入力に応じた注意（gmail以外＝Workspace等の可能性）
    const inviteHint = useMemo(() => {
        const e = inviteEmail.trim();
        if (!e) return null;
        if (isProbablyGmail(e)) return null;
        return "※ Gmail 以外（Google Workspace等）の場合は、そのメールが「Googleアカウントでログイン可能」かをご確認ください。";
    }, [inviteEmail]);

    // -----------------------
    // メンバー一覧取得
    // -----------------------
    const loadUsers = async () => {
        try {
            setError(null);
            const res = await apiGet("/api/admin/users");
            const json = await res.json();
            setUsers(json.users ?? []);
        } catch (e: any) {
            console.error(e);
            setError("ユーザー一覧の取得に失敗しました");
        } finally {
            setLoading(false);
        }
    };

    // -----------------------
    // 承認待ち一覧取得
    // -----------------------
    const loadPending = async () => {
        try {
            setError(null);
            const res = await apiGet("/api/admin/users/pending");
            const json = await res.json();
            setPendingUsers(json.users ?? []);
        } catch (e: any) {
            console.error(e);
            setError("承認待ちユーザーの取得に失敗しました");
        } finally {
            setLoadingPending(false);
        }
    };

    useEffect(() => {
        loadUsers();
        loadPending();
    }, []);

    // -----------------------
    // 承認API
    // -----------------------
    const handleApprove = async (uid: string, email: string | null) => {
        if (!window.confirm(`このユーザーを承認しますか？\n\n対象：${email ?? "（メール不明）"}`)) return;

        try {
            setUpdatingUid(uid);

            const res = await apiPost("/api/admin/users/approve", {
                targetUid: uid,
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                alert("承認に失敗しました：" + (json.error ?? "不明なエラー"));
                return;
            }

            setPendingUsers((prev) => prev.filter((u) => u.uid !== uid));
            await loadUsers();
        } catch (e: any) {
            alert("通信エラー: " + e.message);
        } finally {
            setUpdatingUid(null);
        }
    };

    // -----------------------
    // 招待フォーム
    // -----------------------
    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inviteEmail) {
            alert("メールアドレスを入力してください");
            return;
        }

        // ✅ Gmail固定で弾かない（Workspace等を許容）
        //    ここではあくまで注意書きだけに留める

        try {
            setSaving(true);
            setError(null);

            const res = await apiPost("/api/admin/users/invite", {
                email: inviteEmail,
                role: inviteRole,
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                alert("ユーザー招待に失敗：" + (json.error ?? "不明なエラー"));
                return;
            }

            alert(`${inviteEmail.trim()} に招待を送信しました`);

            setInviteEmail("");
            setInviteRole("member");

            await loadUsers();
            await loadPending();
        } catch (e: any) {
            alert("通信エラー: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    // -----------------------
    // 権限変更
    // -----------------------
    const handleRoleChange = async (uid: string, newRole: string) => {
        if (currentUid && uid === currentUid) {
            alert("自分自身の権限は変更できません");
            return;
        }

        try {
            setUpdatingUid(uid);

            const res = await apiPost("/api/admin/users/update-role", {
                targetUid: uid,
                role: newRole,
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                alert("権限変更に失敗：" + (json.error ?? "不明なエラー"));
                return;
            }

            setUsers((prev) =>
                prev.map((u) => (u.uid === uid ? { ...u, role: newRole } : u))
            );
        } catch (e: any) {
            alert("通信エラー: " + e.message);
        } finally {
            setUpdatingUid(null);
        }
    };

    // -----------------------
    // 削除
    // -----------------------
    const handleDelete = async (uid: string, email: string | null) => {
        if (!window.confirm(`このユーザーを削除しますか？\n\n対象：${email ?? "（メール不明）"}`)) return;

        try {
            setUpdatingUid(uid);

            const res = await apiPost("/api/admin/users/delete", {
                targetUid: uid,
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                alert("削除に失敗：" + (json.error ?? "不明なエラー"));
                return;
            }

            setUsers((prev) => prev.filter((u) => u.uid !== uid));
        } catch (e: any) {
            alert("通信エラー: " + e.message);
        } finally {
            setUpdatingUid(null);
        }
    };

    return (
        <div>
            <SupportAnchorBanner />

            {/* ==============================
                承認待ちユーザー区画
            ===============================*/}
            <div className="mb-8">
                <h2 className="text-lg font-medium mb-3">承認待ちユーザー</h2>

                {loadingPending ? (
                    <LoadingInline />
                ) : pendingUsers.length === 0 ? (
                    <div className="text-sm text-slate-500">
                        承認待ちユーザーはいません。
                    </div>
                ) : (
                    <table className="w-full text-sm border border-slate-200 mb-4">
                        <thead className="bg-yellow-50">
                            <tr>
                                <th className="border border-slate-200 px-2 py-1">
                                    メール
                                </th>
                                <th className="border border-slate-200 px-2 py-1 text-center">
                                    操作
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingUsers.map((u) => (
                                <tr key={u.uid} className="bg-white hover:bg-slate-50">
                                    <td className="border border-slate-200 px-2 py-1 text-left">
                                        {u.email ?? "-"}
                                    </td>
                                    <td className="border border-slate-200 px-2 py-1 text-center">
                                        <button
                                            onClick={() => handleApprove(u.uid, u.email)}
                                            disabled={updatingUid === u.uid}
                                            className="px-3 py-1 bg-green-600 text-white text-xs rounded disabled:opacity-50"
                                        >
                                            承認する
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* 招待フォーム */}
            <form
                onSubmit={handleInvite}
                className="mb-2 flex flex-wrap items-center gap-2"
            >
                <input
                    type="email"
                    className="border rounded px-3 py-2 text-sm w-64"
                    placeholder="user@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                />
                <select
                    className="border rounded px-2 py-2 text-sm"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                >
                    {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>
                            {r.label}
                        </option>
                    ))}
                </select>
                <button
                    type="submit"
                    disabled={saving || !inviteEmail.trim()}
                    className="bg-blue-600 text-white text-sm px-4 py-2 rounded disabled:opacity-50"
                >
                    {saving ? "招待中..." : "ユーザーを招待"}
                </button>
            </form>

            {/* ✅ 表記：Gmailではなく「Googleアカウント」 */}
            <div className="mb-2 text-xs text-slate-500">
                ※ 現在、招待は <span className="font-medium">Googleアカウントでログイン可能なメール</span>
                （Gmail / Google Workspace など）のみ対応しています
            </div>

            {/* ✅ 入力がgmail以外なら注意（弾かない） */}
            {inviteHint && (
                <div className="mb-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    {inviteHint}
                </div>
            )}

            {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

            {loading ? (
                <LoadingInline />
            ) : users.length === 0 ? (
                <div className="text-sm text-slate-500">
                    まだメンバーが登録されていません。
                </div>
            ) : (
                <table className="w-full text-sm border border-slate-200">
                    <thead className="bg-slate-100">
                        <tr>
                            <th className="border border-slate-200 px-2 py-1">名前</th>
                            <th className="border border-slate-200 px-2 py-1">メール</th>
                            <th className="border border-slate-200 px-2 py-1">権限</th>
                            <th className="border border-slate-200 px-2 py-1">最終ログイン</th>
                            <th className="border border-slate-200 px-2 py-1">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.uid} className="bg-white hover:bg-slate-50">
                                <td className="border border-slate-200 px-2 py-1 text-left">
                                    {u.displayName ?? "-"}
                                </td>
                                <td className="border border-slate-200 px-2 py-1 text-left">
                                    {u.email ?? "-"}
                                </td>
                                <td className="border border-slate-200 px-2 py-1 text-center">
                                    <select
                                        className="border rounded px-1 py-1 text-xs"
                                        value={u.role ?? "member"}
                                        disabled={updatingUid === u.uid || (currentUid ? u.uid === currentUid : false)}
                                        onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                                    >
                                        {ROLE_OPTIONS.map((r) => (
                                            <option key={r.value} value={r.value}>
                                                {r.label}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="border border-slate-200 px-2 py-1 text-center">
                                    {u.lastSignInAt
                                        ? new Date(u.lastSignInAt).toLocaleString()
                                        : "-"}
                                </td>
                                <td className="border border-slate-200 px-2 py-1 text-center">
                                    <button
                                        onClick={() => handleDelete(u.uid, u.email)}
                                        disabled={updatingUid === u.uid}
                                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                    >
                                        削除
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <UserManagementSupportInfo />
        </div>
    );
}
